import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Download, FileText, Paperclip, SquarePen, Trash2, Upload } from 'lucide-react';
import { InlineNumberInput } from '../components/inbox/InlineNumberInput';
import { InlineClassSelect } from '../components/inbox/InlineClassSelect';
import { MoveBlockModal } from '../components/MoveBlockModal';
import { BlockViewModal } from '../components/BlockViewModal';
import { ManualAddForm, weekDays } from '../components/ManualAddForm';
import { LineDayEditForm } from '../components/LineDayEditForm';
import { MoneyInput } from '../components/MoneyInput';
import { api, ApiCallError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useDownloadRollupXlsx, usePcoGeneratedDocuments, useRollupDetail, useSubmissions } from '../lib/useRollups';
import { useSettingsProject } from '../lib/useSettings';
import { ConfirmModal } from '../components/ConfirmModal';
import { SendComposerModal, type SendComposerPayload } from '../components/SendComposerModal';
import { PdfPreviewModal } from '../components/PdfPreviewModal';
import { PdfCompareModal } from '../components/PdfCompareModal';
import type { PdfSource } from '../lib/usePdfSignedUrl';
import { useToast } from '../components/ToastProvider';
import {
  derivePdfFilename,
  equipmentLineDisplayKey,
  laborLineDisplayKey,
  type RollupSendDocument,
  type SubmissionHistoryItem,
} from '@coffman-tm/shared';
import {
  Container,
  Dropdown,
  Notice,
  Skeleton,
  StaleDataBanner,
  Stat,
  StatRow,
  ExpandChevron,
  type StatusPillKind,
} from '../components/design';
import { formatDateMd, formatDateShort, formatHours, formatMoneyCents, formatPcoNumber, formatSubmissionLabel, formatSubmissionShort, formatWeekRange, gcName } from '../lib/format';
import { usePcoSubmissions, submissionErrorMessage } from '../lib/useSubmissions';
import {
  dailyDocumentsInSend,
  generatedDocumentCell,
  generatedDocumentsForRollup,
  type GeneratedColumnStatus,
  type SourceComparePair,
} from '../lib/regenerationPlan';
import {
  closedWithoutSendAt,
  latestSendAt,
  rollupPillKind,
  rollupStatusLabel,
} from '../lib/rollupStatus';
import { REVISION_BANNER_LABEL } from '../lib/revisionBanner';
import { EditImportedTotalModal } from '../components/EditImportedTotalModal';
import { RevisionBannerChrome } from '../components/RevisionBannerChrome';
import { RevisionDetailsTip } from '../components/RevisionDetailsTip';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import type {
  ManualProvenance,
  RollupBlockSummary,
  RollupBlocksResponse,
  RollupDetailEquipmentLine,
  RollupDetailLaborLine,
  RollupDetailMaterialLine,
  RollupDetailSubcontractorLine,
  RollupStatus,
  StaleReason,
  SubcontractorInvoiceErrorCode,
  SubcontractorLinePatchRequest,
  SubmissionSummary,
} from '@coffman-tm/shared';
import { SUBCONTRACTOR_INVOICE_MAX_BYTES } from '@coffman-tm/shared';

/**
 * UI-only flag. Coffman/Hoffman 25132 bills total hours at the flat ST rate
 * (project.bill_overtime_as_st), so the OT and DT columns are always zero on
 * this rollup and just add visual noise. Flip back to `true` when a future
 * project actually bills 1.5× / 2× premium; the underlying data and the
 * rate-triple math in compute.ts still carry ST/OT/DT separately, so this
 * is a render decision only.
 */
const SHOW_OT_DT_COLUMNS = false;

/**
 * UI-only flag. The 4-up Labor / Equipment / Materials / Invoice-total band
 * that used to sit above the sections. Off since the sticky totals rail carries
 * those same four numbers permanently — with the rail on screen the band was
 * the same money printed twice, and it cost the top ~90px of every rollup
 * (client, 2026-08-01). Flip to `true` to bring it back; nothing else moves.
 */
const SHOW_STAT_BAND = false;

export function RollupDetailPage() {
  const { me } = useAuth();
  const { pcoId, weekEndingDate } = useParams<{ pcoId: string; weekEndingDate: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Canonical addressing: `?rollup=<id>` names the exact rollup to view. Split
  // weeks require it (two rollups share a (pco, week)); without it the fetch
  // falls back to the week's sole-or-default rollup — identical for non-split.
  const rollupIdParam = searchParams.get('rollup');
  const detail = useRollupDetail(pcoId ?? null, weekEndingDate ?? null, rollupIdParam);
  // One viewer for the whole page: the source-block rows open daily PDFs by id,
  // the subcontractor rows open their vendor invoice by (rollup, line).
  const [previewSource, setPreviewSource] = useState<PdfSource | null>(null);
  const [addForm, setAddForm] = useState<
    'labor' | 'equipment' | 'material' | 'subcontractor' | null
  >(null);
  // The open day-explicit line editor (labor or equipment). `displayKey`
  // identifies which on-screen line was clicked; the form resolves it to the
  // underlying per-day rows and makes the PM name the day.
  const [lineEdit, setLineEdit] = useState<{
    kind: 'labor' | 'equipment';
    lineLabel: string;
    displayKey: string;
    focusField: 'hours' | 'rate';
  } | null>(null);
  const toast = useToast();

  if (detail.isLoading || !detail.data) {
    // Nothing to show yet. If the fetch already failed, say so and offer a way
    // out — a bare "Loading…" over a dead request is a trap.
    const failed = !!detail.error && !detail.isLoading;
    return (
      <Container>
        <div className="page-head">
          <div>
            {failed ? (
              <h1 className="page-title">Couldn’t load this rollup</h1>
            ) : (
              <h1 className="page-title">
                <Skeleton style={{ width: 280, height: 24 }} />
              </h1>
            )}
          </div>
        </div>
        {detail.error ? (
          <>
            <p style={{ color: 'var(--danger)', fontSize: 13 }}>
              {detail.error.message}
            </p>
            <button type="button" className="btn btn-ghost" onClick={() => void detail.refetch()}>
              Retry
            </button>
          </>
        ) : (
          // Both columns sketched, in the shape the loaded page takes: the
          // section stack on the left, the money rail on the right. No stat-band
          // skeleton — there's no band to load into any more.
          <div className="rollup-layout">
            <div className="rollup-sections">
              <LoadingSectionTable
                title="Labor"
                headers={[
                  { label: 'Employee ID' },
                  { label: 'Name' },
                  { label: 'Class' },
                  { label: 'Total hrs', num: true },
                  { label: 'Rate', num: true },
                  { label: 'Line total', num: true },
                ]}
              />
              <LoadingSectionTable
                title="Equipment"
                headers={[
                  { label: 'Asset' },
                  { label: 'Description' },
                  { label: 'Hours', num: true },
                  { label: 'Rate', num: true },
                  { label: 'Line total', num: true },
                ]}
              />
              <LoadingSectionTable
                title="Materials"
                headers={[
                  { label: 'Item' },
                  { label: 'Qty', num: true },
                  { label: 'Rate', num: true },
                  { label: 'Line total', num: true },
                ]}
              />
            </div>
            <div className="rollup-rail">
              <div className="section section--card rollup-totals">
                <div className="section-head">
                  <h2 className="section-title">Invoice totals</h2>
                </div>
                <table className="table">
                  <tbody aria-hidden>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        <td>
                          <Skeleton style={{ width: '70%', height: 12 }} />
                        </td>
                        <td className="num">
                          <Skeleton style={{ width: 72, height: 12 }} />
                        </td>
                      </tr>
                    ))}
                    <tr className="subtotal">
                      <td>
                        <Skeleton style={{ width: 80, height: 13 }} />
                      </td>
                      <td className="num">
                        <Skeleton style={{ width: 96, height: 18 }} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </Container>
    );
  }

  const d = detail.data;
  // Imported (backfill) rollups are frozen history: zero member entries, no
  // editable lines. Hide the line sections + PDF blocks and show the frozen
  // totals with a "Download original" action instead.
  const isImported = d.imported;

  // "+ Add row" header action — only on in_progress rollups (server enforces the
  // locked-rollup guard too). Toggles the inline ManualAddForm for the section.
  // 'subcontractor' additionally requires a persisted rollup id: the endpoint
  // is canonical-only (POST /by-rollup/:id/subcontractors) — a week with no
  // rollup row yet has nothing to hang a rollup-level line on.
  const addAction = (
    section: 'labor' | 'equipment' | 'material' | 'subcontractor',
    label: string,
  ) =>
    d.status === 'in_progress' && (section !== 'subcontractor' || d.rollup_id !== null) ? (
      <AddRowButton label={label} onClick={() => setAddForm(section)} />
    ) : undefined;

  // Page identity is the PCO + week, not the description — the description
  // folds into the subline instead. Same strings/separators the old eyebrow
  // used, just promoted to the title.
  const headTitle = `${formatSubmissionLabel(d.pco.number, d.submission?.submission_number ?? null)} · Week ${formatWeekRange(d.week_ending_date)}`;
  const headSub = [
    d.pco.description,
    d.submission?.mod_number ? `MOD ${d.submission.mod_number}` : null,
    d.submission?.hoffman_fwa ? `FWA ${d.submission.hoffman_fwa}` : null,
    `${d.contributing_pdfs.length} PDF${d.contributing_pdfs.length === 1 ? '' : 's'}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Container>
      {/* A failed refresh with data already on screen used to be invisible.
          Keyed on the error so a fresh failure re-announces itself even if a
          previous one was dismissed. */}
      <StaleDataBanner
        key={detail.error?.message}
        error={detail.error}
        onRetry={() => void detail.refetch()}
      />
      <div className="page-head">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 className="page-title">{headTitle}</h1>
            {/* Silence is the default state — a status chip only earns its
                place once the rollup has left in-progress. */}
            {d.status !== 'in_progress' && (
              <span className={`status-chip ${rollupChipClass(rollupPillKind(d.status))}`}>
                {rollupStatusLabel(d.status)}
              </span>
            )}
          </div>
          <p className="page-sub">{headSub}</p>
        </div>
        {d.submitted_at && (
          <div className="page-meta">
            <div className="pm-row">
              <span className="pm-label">Submitted</span>
              <span className="pm-val">{formatDateShort(d.submitted_at.slice(0, 10))}</span>
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          margin: '12px 0',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <button type="button" className="back-chip" onClick={() => navigate(`/pcos/${d.pco.id}`)}>
          <ArrowLeft size={14} aria-hidden />
          PCO {formatPcoNumber(d.pco.number)}
        </button>
        {/* REOPENED FOR REVISION — beside the back chip, not a strip of its own
            (Brandon 2026-08-19: a full-width band below the toolbar read as a
            shoved-in section). It belongs to this row's LEFT cluster; the
            right cluster is the week's actions, and the download this banner
            points at is one of them. Identical chrome to the week-row banner —
            one component, one --warning border and one --warning-tint fill
            (RULES 2.8) — at the density a toolbar line calls for rather than a
            band. */}
        {d.revision_context && (
          <RevisionBannerChrome density="toolbar">
            <strong style={{ color: 'var(--warning)', fontWeight: 600 }}>
              {REVISION_BANNER_LABEL}
            </strong>
            <RevisionDetailsTip
              context={d.revision_context}
              currentInvoiceTotalCents={d.totals.invoice_total_cents}
              gcLabel={gcName(me)}
              surface="detail-page"
            />
          </RevisionBannerChrome>
        )}
        <div style={{ marginLeft: 'auto' }}>
          <ActionButtons
            pcoId={d.pco.id}
            rollupId={d.rollup_id}
            pcoNumber={d.pco.number}
            submissionNumber={d.submission?.submission_number ?? null}
            weekEndingDate={d.week_ending_date}
            status={d.status}
            imported={d.imported}
            hasOriginalXlsx={d.has_original_xlsx}
            invoiceTotalCents={d.totals.invoice_total_cents}
            onChanged={() => detail.refetch()}
          />
        </div>
      </div>

      {SHOW_STAT_BAND && (
        <StatRow>
          <Stat label="Labor" value={formatMoneyCents(d.totals.labor_cents)} />
          <Stat label="Equipment" value={formatMoneyCents(d.totals.equipment_cents)} />
          <Stat label="Materials" value={formatMoneyCents(d.totals.materials_cents)} />
          <Stat label="Invoice total" value={formatMoneyCents(d.totals.invoice_total_cents)} />
        </StatRow>
      )}

      {/* Two columns from here down: the section stack, and a money rail that
          keeps the invoice ladder pinned while the PM works through the lines.
          Single column below 1100px — see .rollup-layout in index.css. */}
      <div className="rollup-layout">
        <div className="rollup-sections">
          {/* The reopened-for-revision banner is NOT here — it rides the
              toolbar row above, beside the back chip. The notice below fires
              only on a still-LOCKED week, which is the other half of the stale
              story and a different claim. */}
          {d.stale_after_submission && d.status !== 'in_progress' && (
            <Notice>
              {isImported ? (
                // Imported rollups can't reopen (frozen history — the server 409s),
                // so don't suggest it.
                <>Late work arrived for this imported week — bill it through a new submission.</>
              ) : (
                <>
                  This rollup is out of date. {humanizeStaleReason(d.stale_reason)}. The
                  numbers below reflect what was sent to {gcName(me)}; current data has new
                  entries that aren&apos;t included. Reopen to incorporate them.
                </>
              )}
            </Notice>
          )}

          {(d.unknown_employee_count > 0 || d.unknown_equipment_count > 0) && (
            <Notice>
              This rollup includes{' '}
              {d.unknown_employee_count > 0 && (
                <strong>
                  {d.unknown_employee_count} unknown employee
                  {d.unknown_employee_count === 1 ? '' : 's'}
                </strong>
              )}
              {d.unknown_employee_count > 0 && d.unknown_equipment_count > 0 && ' and '}
              {d.unknown_equipment_count > 0 && (
                <strong>
                  {d.unknown_equipment_count} unknown equipment row
                  {d.unknown_equipment_count === 1 ? '' : 's'}
                </strong>
              )}
              {' — their rates contribute $0 to the totals. '}
              <button type="button" className="btn-link" onClick={() => navigate('/settings')}>
                Resolve in Settings
                <ArrowRight size={13} aria-hidden />
              </button>
            </Notice>
          )}

          {!isImported && (
            <>
          <Section title="Labor" headerAction={addAction('labor', 'Add Labor')}>
            {d.labor.length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>No labor on this rollup.</p>
            ) : (() => {
              // Per-row "Line total" cells stay as the integer-cent three-term
              // round so each row reads cleanly. The subtotal row pulls from
              // d.totals.labor_cents (API-side float-sum-then-round) so it
              // matches Joey's Excel ground truth even when individual rows
              // don't add up to it exactly.
              let sumTotalHrs = 0;
              const rows = d.labor.map((l) => {
                const line =
                  Math.round(l.st_hours * l.st_rate_cents) +
                  Math.round(l.ot_hours * l.ot_rate_cents) +
                  Math.round(l.dt_hours * l.dt_rate_cents);
                const totalHrs = l.st_hours + l.ot_hours + l.dt_hours;
                sumTotalHrs += totalHrs;
                return { l, line, totalHrs };
              });
              const editable = d.status === 'in_progress';
              // ST hrs / OT hrs / DT hrs columns dropped — under bill_overtime_as_st
              // (Coffman 25132) ST hrs always equals Total hrs and OT/DT are zero.
              // SHOW_OT_DT_COLUMNS remains live for a future project that bills the
              // premium; if it flips on, add ST hrs back alongside OT/DT.
              return (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Employee ID</th>
                      <th>Name</th>
                      <th>Class</th>
                      {SHOW_OT_DT_COLUMNS && <th className="num">ST hrs</th>}
                      {SHOW_OT_DT_COLUMNS && <th className="num">OT hrs</th>}
                      {SHOW_OT_DT_COLUMNS && <th className="num">DT hrs</th>}
                      <th className="num">Total hrs</th>
                      <th className="num">Rate</th>
                      <th className="num">Line total</th>
                      {editable && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ l, line, totalHrs }, i) => {
                      // The line's identity key — it used to be employee-only and
                      // collided whenever one employee had two lines (day + swing,
                      // or a hand-added line beside a parsed one). It still isn't
                      // unique on its own: compute.ts groups by DAYPART too and this
                      // payload carries none, so a day line and a swing line that
                      // price alike are two rows under one display key. The React
                      // key therefore carries the position as well; the display key
                      // alone stays what identifies the line to the edit form.
                      const displayKey = laborLineDisplayKey(l);
                      return (
                        <LaborRow
                          key={`${displayKey}#${i}`}
                          line={l}
                          totalHrs={totalHrs}
                          lineCents={line}
                          pcoId={pcoId!}
                          weekEndingDate={weekEndingDate!}
                          editable={editable}
                          onSaved={detail.refetch}
                          onEdit={(focusField) =>
                            setLineEdit({
                              kind: 'labor',
                              lineLabel: l.employee_name,
                              displayKey,
                              focusField,
                            })
                          }
                        />
                      );
                    })}
                    <tr className="subtotal">
                      <td colSpan={3}>Labor subtotal</td>
                      {SHOW_OT_DT_COLUMNS && (
                        <td className="num">{formatHours(d.labor.reduce((a, l) => a + l.st_hours, 0))}</td>
                      )}
                      {SHOW_OT_DT_COLUMNS && (
                        <td className="num">{formatHours(d.labor.reduce((a, l) => a + l.ot_hours, 0))}</td>
                      )}
                      {SHOW_OT_DT_COLUMNS && (
                        <td className="num">{formatHours(d.labor.reduce((a, l) => a + l.dt_hours, 0))}</td>
                      )}
                      <td className="num">{formatHours(sumTotalHrs)}</td>
                      <td className="num">—</td>
                      <td className="num">{formatMoneyCents(d.totals.labor_cents)}</td>
                      {editable && <td />}
                    </tr>
                  </tbody>
                </table>
              );
            })()}
          </Section>

          <Section title="Equipment" headerAction={addAction('equipment', 'Add Equipment')}>
            {d.equipment.length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>No equipment on this rollup.</p>
            ) : (() => {
              // Subtotal pulls from d.totals.equipment_cents (API float-sum-
              // then-round); per-row "Line total" still shows integer cents.
              let sumHrs = 0;
              const rows = d.equipment.map((e) => {
                const line = Math.round(e.hours * e.rate_cents);
                sumHrs += e.hours;
                return { e, line };
              });
              const editable = d.status === 'in_progress';
              return (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Asset</th>
                      <th>Description</th>
                      <th className="num">Hours</th>
                      <th className="num">Rate</th>
                      <th className="num">Line total</th>
                      {editable && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ e, line }) => {
                      const displayKey = equipmentLineDisplayKey(e);
                      return (
                        <EquipmentRow
                          key={displayKey}
                          line={e}
                          lineCents={line}
                          editable={editable}
                          onEdit={(focusField) =>
                            setLineEdit({
                              kind: 'equipment',
                              lineLabel: e.asset_id,
                              displayKey,
                              focusField,
                            })
                          }
                        />
                      );
                    })}
                    <tr className="subtotal">
                      <td colSpan={2}>Equipment subtotal</td>
                      <td className="num">{formatHours(sumHrs)}</td>
                      <td className="num">—</td>
                      <td className="num">{formatMoneyCents(d.totals.equipment_cents)}</td>
                      {editable && <td />}
                    </tr>
                  </tbody>
                </table>
              );
            })()}
          </Section>

          <Section title="Materials" headerAction={addAction('material', 'Add Material')}>
            {d.materials.length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>
                No materials.
              </p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="num">Qty</th>
                    <th className="num">Rate</th>
                    <th className="num">Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {d.materials.map((m) => (
                    <MaterialRow
                      key={m.material_queue_item_id}
                      line={m}
                      editable={d.status === 'in_progress'}
                      onSaved={detail.refetch}
                    />
                  ))}
                  <tr className="subtotal">
                    <td colSpan={3}>Materials subtotal</td>
                    <td className="num">{formatMoneyCents(d.totals.materials_cents)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </Section>

          {/* `?? []` / `?? 0` on the subcontractor fields: cheap insurance against
              the brief deploy window where the new web bundle reads a detail from
              the not-yet-redeployed Worker. */}
          <Section title="Subcontractors" headerAction={addAction('subcontractor', 'Add Subcontractor')}>
            {(d.subcontractors ?? []).length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>
                No subcontractors.
              </p>
            ) : (() => {
              const editable = d.status === 'in_progress';
              return (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Date</th>
                      <th className="num">Amount</th>
                      {editable && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {(d.subcontractors ?? []).map((s) => (
                      <SubcontractorRow
                        key={s.id}
                        line={s}
                        rollupId={d.rollup_id}
                        weekEndingDate={d.week_ending_date}
                        editable={editable}
                        onSaved={detail.refetch}
                        onViewInvoice={setPreviewSource}
                      />
                    ))}
                    <tr className="subtotal">
                      <td colSpan={2}>Subcontractors subtotal</td>
                      <td className="num">{formatMoneyCents(d.totals.subcontractors_cents ?? 0)}</td>
                      {editable && <td />}
                    </tr>
                  </tbody>
                </table>
              );
            })()}
          </Section>

          <SourceBlocksSection
            pcoId={d.pco.id}
            weekEndingDate={d.week_ending_date}
            rollupId={d.rollup_id}
            pcoNumber={d.pco.number}
            onPreviewPdf={setPreviewSource}
            onChanged={() => detail.refetch()}
          />
            </>
          )}

          {isImported && (
            <Notice>
              This is an imported historical rollup — the frozen totals below are what
              Coffman sent to {gcName(me)}. Use “Download original” for the source xlsx.
            </Notice>
          )}

          {d.status !== 'in_progress' && (
            <SubmissionHistoryPanel
              pcoId={d.pco.id}
              weekEndingDate={d.week_ending_date}
              rollupId={d.rollup_id}
              status={d.status}
              submittedAt={d.submitted_at}
            />
          )}
        </div>

        {/* Money rail. Same rows, same helpers, same numbers the section
            bottom used to carry — relocated so the total never scrolls off. */}
        <div className="rollup-rail">
          <div className="section section--card rollup-totals">
            <div className="section-head">
              <h2 className="section-title">Invoice totals</h2>
            </div>
            <table className="table">
              <tbody>
                {/* Bare bucket names (Brandon 2026-08-05, same ruling as the
                    PCO Totals card) — "subtotal" on every line was redundant;
                    the one line that IS the subtotal keeps the word. */}
                <tr>
                  <td>Labor</td>
                  <td className="num">{formatMoneyCents(d.totals.labor_cents)}</td>
                </tr>
                <tr>
                  <td>Equipment</td>
                  <td className="num">{formatMoneyCents(d.totals.equipment_cents)}</td>
                </tr>
                <tr>
                  <td>Materials</td>
                  <td className="num">{formatMoneyCents(d.totals.materials_cents)}</td>
                </tr>
                {(d.totals.subcontractors_cents ?? 0) !== 0 && (
                  <tr>
                    <td>Subcontractors</td>
                    <td className="num">{formatMoneyCents(d.totals.subcontractors_cents)}</td>
                  </tr>
                )}
                {d.equipment_tax_pct > 0 && (
                  <tr>
                    {/* GC wording — the xlsx and cover letter print "Equip. Markup"
                        for this line; the field stays equipment_tax_*. */}
                    <td>Equip. markup ({d.equipment_tax_pct}%)</td>
                    <td className="num">{formatMoneyCents(d.totals.equipment_tax_cents)}</td>
                  </tr>
                )}
                <tr>
                  <td>
                    Markup ({d.markup_pct}%)
                  </td>
                  <td className="num">{formatMoneyCents(d.totals.markup_cents)}</td>
                </tr>
                <tr>
                  <td>
                    Per diem (@{' '}
                    {formatMoneyCents(d.per_diem_rate_cents, { precise: true })})
                  </td>
                  <td className="num">{formatMoneyCents(d.totals.per_diem_cents)}</td>
                </tr>
                <tr className="subtotal">
                  <td>Invoice total</td>
                  <td className="num">{formatMoneyCents(d.totals.invoice_total_cents)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <PdfPreviewModal source={previewSource} onClose={() => setPreviewSource(null)} />

      {addForm && (
        <ManualAddForm
          section={addForm}
          pcoId={d.pco.id}
          weekEndingDate={d.week_ending_date}
          rollupId={d.rollup_id}
          onClose={() => setAddForm(null)}
          onAdded={detail.refetch}
        />
      )}

      {lineEdit && (
        <LineDayEditForm
          kind={lineEdit.kind}
          lineLabel={lineEdit.lineLabel}
          displayKey={lineEdit.displayKey}
          focusField={lineEdit.focusField}
          pcoId={d.pco.id}
          weekEndingDate={d.week_ending_date}
          rollupId={d.rollup_id}
          onClose={() => setLineEdit(null)}
          onSaved={async (summary) => {
            await detail.refetch();
            // Say what moved and on which day — the point of the whole form is
            // that the day is no longer a guess, so it gets said out loud.
            toast.push({ variant: 'success', title: 'Line updated', body: summary });
          }}
        />
      )}
    </Container>
  );
}

function Section({
  title,
  defaultExpanded = false,
  headerAction,
  children,
}: {
  title: string;
  /** Initial expanded state. Defaults to false — every section on this page
   *  opens closed so Joey gets a clean overview and expands what he wants to
   *  inspect. */
  defaultExpanded?: boolean;
  /** Optional action rendered in the header next to the toggle (e.g. "+ Add row").
   *  Its clicks don't toggle the section. */
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);

  // `is-expanded` is what buys the head its bottom margin — a closed section is
  // a single tight row, not a box with a title floating in it.
  return (
    <div className={`section section--card section--card--slim${expanded ? ' is-expanded' : ''}`}>
      <div
        className="section-head clickable"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
        title={expanded ? 'Click to collapse' : 'Click to expand'}
      >
        <div className="section-head-left">
          <span style={{ color: 'var(--ink-3)', display: 'inline-flex' }}>
            <ExpandChevron expanded={expanded} size={14} />
          </span>
          <h2 className="section-title">{title}</h2>
        </div>
        {headerAction ? (
          <span onClick={(e) => e.stopPropagation()} role="presentation">
            {headerAction}
          </span>
        ) : null}
      </div>
      {expanded && children}
    </div>
  );
}

/**
 * Section-shaped table skeleton for the page-level loading state. The real
 * Labor/Equipment/Materials sections open collapsed, but there's nothing to
 * collapse before the rollup has even loaded — this renders the table shape
 * open instead: real headers (known statically, independent of the fetch)
 * over skeleton rows sized per column, right-aligned bars in the num cells.
 */
function LoadingSectionTable({
  title,
  headers,
}: {
  title: string;
  headers: Array<{ label: string; num?: boolean }>;
}) {
  return (
    <div className="section section--card section--card--slim is-expanded">
      <div className="section-head">
        <h2 className="section-title">{title}</h2>
      </div>
      <table className="table">
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h.label} className={h.num ? 'num' : undefined}>
                {h.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody aria-hidden>
          {Array.from({ length: 3 }).map((_, i) => (
            <tr key={i}>
              {headers.map((h, j) => (
                <td key={h.label} className={h.num ? 'num' : undefined}>
                  <Skeleton style={{ width: h.num ? 60 : j === 0 ? '70%' : '50%', height: 12 }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActionButtons({
  pcoId,
  rollupId,
  pcoNumber,
  submissionNumber,
  weekEndingDate,
  status,
  imported,
  hasOriginalXlsx,
  invoiceTotalCents,
  onChanged,
}: {
  pcoId: string;
  /** Canonical rollup id from the detail. When present, send/reopen/download
   *  target this exact rollup (split-week safe); null falls back to legacy. */
  rollupId: string | null;
  pcoNumber: string;
  submissionNumber: string | null;
  weekEndingDate: string;
  status: RollupStatus;
  /** Backfill-imported rollup: frozen history, no reopen (server 409s). */
  imported: boolean;
  /** An original xlsx is stored in R2 — the download serves it verbatim. */
  hasOriginalXlsx: boolean;
  invoiceTotalCents: number;
  onChanged: () => Promise<unknown>;
}) {
  const { token, me } = useAuth();
  const project = useSettingsProject();
  const downloadXlsx = useDownloadRollupXlsx();
  const toast = useToast();
  const [dlBusy, setDlBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [reopenBusy, setReopenBusy] = useState(false);
  const [closeBusy, setCloseBusy] = useState(false);
  const [confirmKind, setConfirmKind] = useState<'send' | 'reopen' | 'close' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // "Close without sending" is only a thing on a week the GC has ALREADY been
  // sent a copy of, so the bar has to know whether this week has any sends.
  // Its own read rather than a prop threaded from the page root: the send list
  // is one indexed query and the alternative is passing a query result down
  // through the section stack to three unrelated consumers.
  const sends = useSubmissions(pcoId, weekEndingDate, rollupId);
  const lastSentAt = latestSendAt(sends.data?.submissions ?? []);

  const recipient = project.data?.gc_email ?? '';
  const additionalRecipients = project.data?.gc_additional_emails ?? [];

  const handleDownload = async () => {
    setDlBusy(true);
    setErr(null);
    try {
      await downloadXlsx(pcoId, weekEndingDate, {
        rollupId,
        fallback: { pco_number: pcoNumber, submission_number: submissionNumber },
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setDlBusy(false);
    }
  };

  const doSend = async (payload: SendComposerPayload) => {
    if (!token) return;
    setSendBusy(true);
    setErr(null);
    try {
      const result = rollupId
        ? await api.rollups.submitById(token, rollupId, payload)
        : await api.rollups.submit(token, pcoId, weekEndingDate, payload);
      setConfirmKind(null);
      toast.push({
        variant: 'success',
        title: 'Sent',
        body: `Sent to ${result.recipient_email}. ${result.attached_pdf_count} PDF${result.attached_pdf_count === 1 ? '' : 's'} attached. Invoice total ${(result.invoice_total_cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}.`,
        durationMs: 6000,
      });
      await onChanged();
    } catch (e) {
      // Prefer the server's friendly `message` (e.g. the pm_email_required
      // "Configure PM in Project Settings before sending.") over the bare code.
      if (
        e instanceof ApiCallError &&
        e.payload &&
        typeof e.payload === 'object' &&
        'message' in e.payload &&
        typeof (e.payload as { message?: unknown }).message === 'string'
      ) {
        setErr((e.payload as { message: string }).message);
      } else {
        setErr(e instanceof Error ? e.message : 'Send failed');
      }
    } finally {
      setSendBusy(false);
    }
  };

  const doReopen = async () => {
    if (!token) return;
    setReopenBusy(true);
    setErr(null);
    try {
      if (rollupId) await api.rollups.reopenById(token, rollupId);
      else await api.rollups.reopen(token, pcoId, weekEndingDate);
      setConfirmKind(null);
      await onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Reopen failed');
    } finally {
      setReopenBusy(false);
    }
  };

  const doCloseWithoutSend = async () => {
    if (!token) return;
    setCloseBusy(true);
    setErr(null);
    try {
      if (rollupId) await api.rollups.closeWithoutSendById(token, rollupId);
      else await api.rollups.closeWithoutSend(token, pcoId, weekEndingDate);
      setConfirmKind(null);
      toast.push({
        variant: 'success',
        title: 'Closed without sending',
        body: 'This revision is the version of record. No email went out.',
        durationMs: 5000,
      });
      await onChanged();
    } catch (e) {
      if (
        e instanceof ApiCallError &&
        e.payload &&
        typeof e.payload === 'object' &&
        'message' in e.payload &&
        typeof (e.payload as { message?: unknown }).message === 'string'
      ) {
        setErr((e.payload as { message: string }).message);
      } else {
        setErr(e instanceof Error ? e.message : 'Close failed');
      }
    } finally {
      setCloseBusy(false);
    }
  };

  const isInProgress = status === 'in_progress';
  const isSubmitted = status === 'submitted';
  const [editTotalOpen, setEditTotalOpen] = useState(false);
  // Offered only where it means something: an open week the GC has already
  // been sent a copy of. A never-sent week still has to go out for real, which
  // the server enforces too (`409 never_sent`) — this only keeps the PM from
  // meeting that refusal.
  const canCloseWithoutSend = isInProgress && !imported && lastSentAt !== null;

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      {err && <span style={{ color: 'var(--danger)', fontSize: 12 }}>{err}</span>}
      {/* An imported week's only mutation. Its bar is otherwise just the
          download — Reopen is refused on these (no lines to rebuild from,
          register I1), which is why the week row offers this in Reopen's place
          too. Same modal on both surfaces. */}
      {imported && rollupId && (
        <button type="button" className="btn btn-ghost" onClick={() => setEditTotalOpen(true)}>
          Edit total
        </button>
      )}
      {imported && rollupId && editTotalOpen && (
        <EditImportedTotalModal
          rollupId={rollupId}
          weekEndingDate={weekEndingDate}
          currentTotalCents={invoiceTotalCents}
          onCancel={() => setEditTotalOpen(false)}
          onSaved={async () => {
            setEditTotalOpen(false);
            await onChanged();
          }}
        />
      )}
      <button
        type="button"
        className="btn btn-ghost"
        onClick={handleDownload}
        disabled={dlBusy}
        style={{
          background: 'var(--surface)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <Download size={14} aria-hidden />
        {dlBusy ? 'Downloading…' : hasOriginalXlsx ? 'Download original' : 'Download xlsx'}
      </button>
      {isSubmitted && !imported && (
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => setConfirmKind('reopen')}
          disabled={reopenBusy}
        >
          {reopenBusy ? 'Reopening…' : 'Reopen rollup'}
        </button>
      )}
      {canCloseWithoutSend && (
        // Ghost, not accent: Approve & Send holds the one solid primary on
        // this bar (RULES 2.1), and this is the quieter of the two ways out of
        // a reopened week.
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => setConfirmKind('close')}
          disabled={closeBusy}
          style={{ background: 'var(--surface)' }}
        >
          {closeBusy ? 'Closing…' : 'Close without sending'}
        </button>
      )}
      {isInProgress && (
        <button
          type="button"
          className="btn btn-cta"
          onClick={() => setConfirmKind('send')}
          disabled={sendBusy}
        >
          {sendBusy ? 'Sending…' : 'Approve & Send'}
        </button>
      )}

      <SendComposerModal
        open={confirmKind === 'send'}
        pcoId={pcoId}
        weekEndingDate={weekEndingDate}
        rollupId={rollupId}
        pcoNumber={pcoNumber}
        submissionLabel={submissionNumber ? formatSubmissionShort(pcoNumber, submissionNumber) : ''}
        gcLabel={gcName(me)}
        invoiceTotalCents={invoiceTotalCents}
        primaryEmail={recipient}
        rosterEmails={additionalRecipients}
        busy={sendBusy}
        error={err}
        onSend={doSend}
        onCancel={() => setConfirmKind(null)}
      />

      <ConfirmModal
        open={confirmKind === 'reopen'}
        title="Reopen this rollup?"
        body={
          <div>
            The rollup goes back to in-progress so you can edit. Any data that
            arrived since you sent it gets pulled in. You'll need to send a
            revised submission to {gcName(me)} afterward.
          </div>
        }
        confirmLabel="Reopen"
        busyLabel="Reopening…"
        busy={reopenBusy}
        onConfirm={doReopen}
        onCancel={() => setConfirmKind(null)}
      />

      <ConfirmModal
        open={confirmKind === 'close'}
        title="Close without sending?"
        body={
          <div>
            This revision is recorded as the version of record — its total goes back
            into Submitted to date and the week locks again.{' '}
            {lastSentAt && (
              <>
                The GC keeps the {formatDateShort(lastSentAt.slice(0, 10))} copy already
                delivered.{' '}
              </>
            )}
            No email goes out and nothing is added to Send history.
          </div>
        }
        confirmLabel="Close without sending"
        busyLabel="Closing…"
        busy={closeBusy}
        onConfirm={doCloseWithoutSend}
        onCancel={() => setConfirmKind(null)}
      />
    </div>
  );
}

function humanizeStaleReason(reason: StaleReason | null): string {
  switch (reason) {
    case 'late_pdf_received':
      return 'A foreman PDF arrived after this rollup was submitted';
    case 'code_remapped':
      return 'An activity code mapped to this PCO was reassigned';
    case 'material_confirmed_after_submit':
      return 'Materials were confirmed after this rollup was submitted';
    case 'block_reassigned_out':
      return 'A block was reassigned off this PCO — this rollup is over-billed until you reopen and resubmit a corrected version';
    case 'block_reassigned_in':
      return 'A block was reassigned into this PCO — this rollup is under-billed until you reopen and resubmit';
    case 'rate_changed':
      return 'A project billing rate (markup, equipment tax, or per-diem) changed after this rollup was sent — reopen and resubmit to rebill at the new rate';
    default:
      return 'The underlying data has changed since this rollup was sent';
  }
}

/**
 * `.status-chip s-*` suffix for a rollup's StatusPillKind — same "map a kind
 * onto the shared page-head chip palette" move as derivedChipClass in
 * lib/pcoStatus.ts. 'ready' (Approved/Paid) reads green, 'progress'
 * (Submitted) reads accent. 'neutral' isn't reachable for RollupStatus today
 * but the switch stays exhaustive rather than assuming that never changes.
 */
function rollupChipClass(kind: StatusPillKind): string {
  switch (kind) {
    case 'ready':
      return 's-approved';
    case 'progress':
      return 's-active';
    default:
      return 's-pending_approval';
  }
}

/**
 * Send history panel — renders the audit trail of every Approve & Send
 * for this rollup. Each Approve & Send creates a `rollup_sends` row; reopening
 * a rollup doesn't delete prior rows. When a rollup is resent after a reopen,
 * the new row is flagged is_revision=true and supersedes_submission_id links
 * back to the prior. The xlsx blob for each send is preserved in R2.
 *
 * Collapsed by default when only one submission exists (the original send);
 * expanded by default when there are revisions because that's the interesting
 * case Joey opens this page to inspect.
 */
function SubmissionHistoryPanel({
  pcoId,
  weekEndingDate,
  rollupId,
  status,
  submittedAt,
}: {
  pcoId: string;
  weekEndingDate: string;
  /** Canonical rollup id — the legacy (pco, week) address resolves the week's
   *  FIRST rollup, which is an arbitrary one of two on a split week. */
  rollupId: string | null;
  /** The two columns the closed-without-sending marker is derived from — see
   *  `closedWithoutSendAt`. Passed rather than re-fetched: the page holds the
   *  detail these come off. */
  status: RollupStatus;
  submittedAt: string | null;
}) {
  const { data, isLoading } = useSubmissions(pcoId, weekEndingDate, rollupId);
  const items = data?.submissions ?? [];
  const hasRevisions = items.some((s) => s.is_revision);
  const [open, setOpen] = useState<boolean>(hasRevisions);
  // The week stands as the version of record, but no copy of THIS version was
  // ever emailed — the PM closed it without sending. Rendered whether the
  // panel is open or shut, because it is the one fact about this week's
  // relationship with the GC that the table below cannot state.
  const gcCopyAt = closedWithoutSendAt({ status, submitted_at: submittedAt }, items);

  if (isLoading || items.length === 0) return null;

  return (
    <div className={`section section--card section--card--slim${open ? ' is-expanded' : ''}`}>
      <div
        className="section-head clickable"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <div className="section-head-left">
          <span style={{ color: 'var(--ink-3)', display: 'inline-flex' }}>
            <ExpandChevron expanded={open} size={14} />
          </span>
          <h2 className="section-title">Send history</h2>
          {!open && (
            <span className="muted" style={{ fontSize: 13 }}>
              {items.length} send{items.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </div>
      {gcCopyAt && (
        <p className="muted" style={{ fontSize: 12.5, marginTop: 8, marginBottom: 0 }}>
          Revised after the last send — the GC has the{' '}
          {formatDateShort(gcCopyAt.slice(0, 10))} version. Reopen the week and send if
          the GC needs this revision.
        </p>
      )}
      {open && (
        <p className="muted" style={{ fontSize: 12.5, marginTop: 8, marginBottom: 10 }}>
          Every Approve &amp; Send creates a row; closing a week without sending creates
          none, so this list is deliveries only. Revisions supersede prior submissions in
          accounting terms, but every prior xlsx + email is preserved as the audit record
          of what was sent.
        </p>
      )}
      {open && <SubmissionHistoryTable items={items} />}
    </div>
  );
}

/**
 * What one attachment WAS, in the Sources tab's vocabulary.
 *
 * Same words the PM read in the send composer's badges and reads again on the
 * "Generated PDF" column, because these are all the same fact — which copy of
 * a page the GC is holding — and three wordings would read as three facts.
 */
function sendDocumentLabel(doc: RollupSendDocument): string {
  if (doc.kind === 'workbook') return 'workbook';
  if (doc.kind === 'invoice') return 'vendor invoice';
  return doc.source === 'regenerated' ? 'regenerated' : 'sent as original';
}

function SubmissionHistoryTable({ items }: { items: SubmissionHistoryItem[] }) {
  const { token, me } = useAuth();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Which sends have their attachment list open. Collapsed by default — the
  // list is 20+ lines on a real week, and the row's job is the send.
  const [openDocs, setOpenDocs] = useState<ReadonlySet<string>>(() => new Set());
  // Original-vs-sent compare for a regenerated attachment. Same viewer, same
  // pane wiring, and the same one-filename-on-both-panes rule as the Sources
  // tab and the week page's own column.
  const [comparePair, setComparePair] = useState<SourceComparePair | null>(null);

  const toggleDocs = (id: string) =>
    setOpenDocs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const download = async (s: SubmissionHistoryItem) => {
    if (!token) return;
    setBusyId(s.id);
    setErr(null);
    try {
      const blob = await api.rollups.downloadSubmissionXlsx(token, s.id);
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        const stamp = s.submitted_at.slice(0, 10);
        a.download = `submission-${stamp}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <table className="table">
        <thead>
          <tr>
            <th>Sent</th>
            <th>Recipient</th>
            <th>Subject</th>
            <th className="num">PDFs</th>
            <th>Status</th>
            <th />
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <Fragment key={s.id}>
              <tr>
                <td className="muted">{formatDateShort(s.submitted_at.slice(0, 10))}</td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {s.recipient_email}
                  {/* Who was COPIED, under who was addressed — quieter, and only
                      when there was one. Sends that predate the column carry null
                      and print nothing rather than an empty "Cc". */}
                  {s.cc_email && (
                    <div style={{ color: 'var(--ink-3)', fontSize: 11, marginTop: 2 }}>
                      Cc {s.cc_email}
                    </div>
                  )}
                </td>
                <td>
                  {s.is_revision && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: 'var(--warning)',
                        background: 'var(--surface-warm)',
                        border: '1px solid var(--rule-strong)',
                        padding: '1px 6px',
                        borderRadius: 3,
                        marginRight: 8,
                      }}
                    >
                      Revision
                    </span>
                  )}
                  {!s.is_revision && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: 'var(--ink-3)',
                        border: '1px solid var(--rule)',
                        padding: '1px 6px',
                        borderRadius: 3,
                        marginRight: 8,
                      }}
                    >
                      Original
                    </span>
                  )}
                  <span style={{ fontSize: 13 }}>{s.subject}</span>
                  {/* What this email actually carried, under what it said —
                      quieter than the subject, same as the Cc line under the
                      recipients. A send with no capture says so plainly rather
                      than offering an empty list. */}
                  <div style={{ marginTop: 3 }}>
                    {s.documents === null ? (
                      <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>
                        Attachment list not recorded
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="btn-link"
                        style={{ fontSize: 11 }}
                        aria-expanded={openDocs.has(s.id)}
                        onClick={() => toggleDocs(s.id)}
                      >
                        {openDocs.has(s.id) ? 'Hide' : 'Show'} {s.documents.length} attachment
                        {s.documents.length === 1 ? '' : 's'}
                      </button>
                    )}
                  </div>
                </td>
                <td className="num">{s.attached_pdf_count}</td>
                <td className="muted" style={{ fontSize: 12 }}>{s.delivery_status}</td>
                <td className="num">
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => void download(s)}
                    disabled={busyId === s.id}
                  >
                    {busyId === s.id ? (
                      'Downloading…'
                    ) : (
                      <>
                        Download xlsx
                        <Download size={13} aria-hidden />
                      </>
                    )}
                  </button>
                </td>
                <td className="num">
                  {err && busyId === null && (
                    <span style={{ color: 'var(--danger)', fontSize: 12 }}>{err}</span>
                  )}
                </td>
              </tr>
              {s.documents !== null && openDocs.has(s.id) && (
                <tr>
                  {/* Full-width addendum belonging to the row above — the
                      `.week-row-extra` convention in table form. */}
                  <td colSpan={7} style={{ paddingTop: 0 }}>
                    <ul
                      style={{
                        listStyle: 'none',
                        margin: 0,
                        padding: '0 0 4px 0',
                        display: 'grid',
                        gap: 4,
                      }}
                    >
                      {s.documents.map((doc, i) => (
                        <li
                          key={`${doc.kind}-${doc.filename}-${i}`}
                          style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 10,
                            fontSize: 12,
                          }}
                        >
                          <span
                            className="mono"
                            style={{ flex: '1 1 auto', minWidth: 0, overflowWrap: 'anywhere' }}
                          >
                            {doc.filename}
                          </span>
                          <span className="muted" style={{ flex: '0 0 auto' }}>
                            {sendDocumentLabel(doc)}
                          </span>
                          {/* Only a regenerated page has a second document to set
                              beside the original. Both panes carry the same
                              filename on purpose — the send attached the rebuilt
                              copy under the original's name. */}
                          <span style={{ flex: '0 0 auto', minWidth: 62, textAlign: 'right' }}>
                            {doc.kind === 'daily' &&
                              doc.source === 'regenerated' &&
                              doc.daily_pdf_id &&
                              doc.generated_document_id && (
                                <button
                                  type="button"
                                  className="btn-link"
                                  onClick={() =>
                                    setComparePair({
                                      dailyPdfId: doc.daily_pdf_id!,
                                      artifactId: doc.generated_document_id!,
                                      filename: doc.filename,
                                    })
                                  }
                                >
                                  Compare
                                </button>
                              )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
      {comparePair && (
        <PdfCompareModal
          left={{
            pdfId: comparePair.dailyPdfId,
            filename: comparePair.filename,
            badge: 'original',
          }}
          right={{
            pdfId: { kind: 'generated', id: comparePair.artifactId },
            filename: comparePair.filename,
            badge: `sent to ${gcName(me)}`,
          }}
          onClose={() => setComparePair(null)}
        />
      )}
    </>
  );
}

/**
 * PDF Source Blocks (ADR 0003). One row per block that belongs to THIS rollup,
 * identified by its source PDF (date / foreman / filename) like the old PDFs
 * section. Two actions per row: View PDF (preview) and View Block (read-only
 * inbox-style detail). Reassignment happens from inside the block view, after
 * the PM has confirmed it's the right block. Collapsed by default.
 */
function SourceBlocksSection({
  pcoId,
  weekEndingDate,
  rollupId,
  pcoNumber,
  onPreviewPdf,
  onChanged,
}: {
  pcoId: string;
  weekEndingDate: string;
  /** Canonical rollup id — the blocks list targets this exact rollup's
   *  pin-aware membership (split-week safe). null falls back to the legacy
   *  (pco, week) route. */
  rollupId: string | null;
  pcoNumber: string;
  onPreviewPdf: (dailyPdfId: string) => void;
  onChanged: () => Promise<unknown>;
}) {
  const { token, me } = useAuth();
  const [data, setData] = useState<RollupBlocksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [viewBlockId, setViewBlockId] = useState<string | null>(null);
  const [reassignBlock, setReassignBlock] = useState<RollupBlockSummary | null>(null);
  const [moveSubmissionBlock, setMoveSubmissionBlock] = useState<RollupBlockSummary | null>(null);
  // Original-vs-generated compare pair for the "Generated PDF" column below —
  // shared shape (SourceComparePair, lib/regenerationPlan.ts) and same viewer
  // (PdfCompareModal) as PcoDetailPage.tsx's Sources tab.
  const [comparePair, setComparePair] = useState<SourceComparePair | null>(null);

  // Carve-out mover: only offered when this PCO has more than one open
  // submission and the week is still active (assign rejects locked weeks).
  const subs = usePcoSubmissions(pcoId);
  const openSubmissions = (subs.data?.submissions ?? []).filter((s) => s.status === 'open');
  const pcoParentNumber = subs.data?.pco_number ?? pcoNumber;
  const canMoveToSubmission =
    openSubmissions.length > 1 && data?.rollup_status === 'in_progress';

  // The "Generated PDF" column's data, from TWO reads, because the column
  // makes two different claims and only one of them is an artifact.
  //
  // 1. The PCO-scoped artifact endpoint the Sources tab also reads
  //    (GET /api/rollups/pco/:pcoId/generated-documents). It covers every
  //    contributing daily across every rollup of the PCO, not just this
  //    week's, so `generatedDocumentsForRollup` narrows it to artifacts
  //    naming THIS rollup — see its doc comment for why a naive
  //    daily_pdf_id → doc map leaks a moved block's OLD week onto its new one.
  // 2. This rollup's own send history, for its per-send document capture — the
  //    RECEIPT for what the GC is holding. It OUTRANKS the artifact wherever it
  //    speaks: an artifact only proves some past send carried those bytes, and
  //    a later send can have attached something else. It also carries the one
  //    claim the artifact endpoint can never make, because an untouched daily
  //    produces no artifact at all. That is what retired 11-intentional.md M14.
  //
  // The LATEST send is the one consulted (the route orders newest first): it is
  // the email that currently stands.
  //
  // Both are unconditional (not gated on the section being expanded), same as
  // the blocks fetch above: this component is the week page's own scope, not a
  // tab shared with other weeks.
  const generatedDocs = usePcoGeneratedDocuments(pcoId);
  const sends = useSubmissions(pcoId, weekEndingDate, rollupId);
  const generatedByDaily = generatedDocumentsForRollup(
    generatedDocs.data?.documents ?? [],
    rollupId,
    dailyDocumentsInSend(sends.data?.submissions[0] ?? null),
  );
  // Mirrors PcoDetailPage.tsx's SourcePdfsTable honesty rule verbatim: only
  // 'ready' may render '—', which is the claim "never part of a send" — the
  // loading and error states have no evidence for that claim. BOTH reads gate
  // it, because either one arriving late would print that em dash over an
  // answer still in flight.
  const generatedStatus: GeneratedColumnStatus =
    generatedDocs.error || sends.error
      ? 'error'
      : generatedDocs.data === null || sends.data === null
        ? 'loading'
        : 'ready';

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErr(null);
    try {
      setData(
        rollupId
          ? await api.rollups.blocksById(token, rollupId)
          : await api.rollups.blocks(token, pcoId, weekEndingDate),
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load blocks');
    } finally {
      setLoading(false);
    }
  }, [token, pcoId, weekEndingDate, rollupId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The row behind the open modal — every prop that needs row data reads
  // off this one lookup.
  const viewedBlock =
    viewBlockId !== null
      ? (data?.blocks.find((b) => b.daily_entry_id === viewBlockId) ?? null)
      : null;

  return (
    <Section title="PDF Source Blocks">
      {loading ? (
        <table className="table">
          <thead>
            <tr>
              <th>Report date</th>
              <th>Foreman</th>
              <th>Original PDF</th>
              <th>Generated PDF</th>
              <th>Activity code</th>
              <th />
            </tr>
          </thead>
          <tbody aria-hidden>
            {Array.from({ length: 3 }).map((_, i) => (
              <tr key={i}>
                <td>
                  <Skeleton style={{ width: 70, height: 12 }} />
                </td>
                <td>
                  <Skeleton style={{ width: '60%', height: 12 }} />
                </td>
                <td>
                  <Skeleton style={{ width: '80%', height: 12 }} />
                </td>
                <td>
                  <Skeleton style={{ width: '60%', height: 12 }} />
                </td>
                <td>
                  <Skeleton style={{ width: 90, height: 12 }} />
                </td>
                <td className="num">
                  <Skeleton style={{ width: 40, height: 12 }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : err ? (
        <p style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</p>
      ) : !data || data.blocks.length === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>
          No blocks on this rollup.
        </p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Report date</th>
              <th>Foreman</th>
              <th>Original PDF</th>
              <th>Generated PDF</th>
              <th>Activity code</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.blocks.map((b) => {
              const filename = derivePdfFilename({
                email_subject: b.email_subject,
                foreman_name: b.foreman_name,
                report_date: b.report_date,
              });
              // Same helper and same honesty rule as the Sources tab's
              // "Generated PDF" column (PcoDetailPage.tsx's SourcePdfsTable).
              const cell = generatedDocumentCell(generatedByDaily.get(b.daily_pdf_id));
              return (
                <tr key={b.daily_entry_id}>
                  <td className="muted">{formatDateShort(b.report_date)}</td>
                  <td className="serif">{b.foreman_name ?? '—'}</td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {filename}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {generatedStatus !== 'ready' ? (
                      ''
                    ) : (
                      <>
                        {/* Dated because the reasons beside it are recomputed
                            from today's data while the page itself is frozen
                            bytes. */}
                        {cell.generatedAt &&
                          `${formatDateMd(cell.generatedAt.slice(0, 10))} · `}
                        {cell.label}
                      </>
                    )}
                  </td>
                  <td className="mono">{b.activity_code}</td>
                  {/* One action per row (client, 2026-07-29). Three buttons
                      per row asked the PM to choose before seeing the block;
                      View PDF, Transfer Block and Change date all live in the
                      block modal's footer now, where the block is on screen to
                      judge. */}
                  <td className="num" style={{ whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => setViewBlockId(b.daily_entry_id)}
                    >
                      View
                      <ArrowRight size={13} aria-hidden />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* The Generated column is the only thing this fetch feeds, so its
          failure is reported here rather than over the whole section — the
          rest of the table is still true. Mirrors PcoDetailPage.tsx's Sources
          tab verbatim. */}
      {generatedStatus === 'error' && (
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Couldn’t load the generated copies, so that column is blank — it
          isn’t saying these reports were never sent.{' '}
          <button
            type="button"
            className="btn-link"
            onClick={() => {
              void generatedDocs.refetch();
              void sends.refetch();
            }}
          >
            Retry
          </button>
        </p>
      )}

      <BlockViewModal
        open={viewBlockId !== null}
        dailyEntryId={viewBlockId}
        canReassign={data?.reassignable ?? false}
        canMoveToSubmission={canMoveToSubmission}
        dailyPdfId={viewedBlock?.daily_pdf_id ?? null}
        reportDate={viewedBlock?.report_date ?? null}
        foremanName={viewedBlock?.foreman_name ?? null}
        onViewPdf={() => {
          // Same branch the Sources tab's single "View PDF" button runs: a
          // generated copy exists → compare it against the original in the
          // same PdfCompareModal; otherwise the original alone, exactly as
          // before this column existed. "One action per row" (2026-07-29)
          // still holds — this is the block modal's own "View PDF", not a
          // second row action.
          if (!viewedBlock?.daily_pdf_id) return;
          const pdfId = viewedBlock.daily_pdf_id;
          const cell = generatedDocumentCell(generatedByDaily.get(pdfId));
          if (cell.artifactId) {
            setComparePair({
              dailyPdfId: pdfId,
              artifactId: cell.artifactId,
              filename: derivePdfFilename({
                email_subject: viewedBlock.email_subject,
                foreman_name: viewedBlock.foreman_name,
                report_date: viewedBlock.report_date,
              }),
            });
          } else {
            onPreviewPdf(pdfId);
          }
        }}
        onMoveToSubmission={() => {
          setViewBlockId(null);
          setMoveSubmissionBlock(viewedBlock);
        }}
        onClose={() => setViewBlockId(null)}
        onReassign={() => {
          setViewBlockId(null);
          setReassignBlock(viewedBlock);
        }}
      />

      <MoveBlockModal
        open={reassignBlock !== null}
        block={reassignBlock}
        sourcePcoNumber={pcoNumber}
        onCancel={() => setReassignBlock(null)}
        onMoved={async () => {
          setReassignBlock(null);
          await load();
          await onChanged();
        }}
      />

      {moveSubmissionBlock && (
        <MoveToSubmissionModal
          block={moveSubmissionBlock}
          submissions={openSubmissions}
          pcoParentNumber={pcoParentNumber}
          onCancel={() => setMoveSubmissionBlock(null)}
          onMoved={async () => {
            setMoveSubmissionBlock(null);
            await Promise.all([load(), subs.refetch(), onChanged()]);
          }}
        />
      )}

      {/* Original left, what the GC actually received right. Same component
          and same pane wiring as PcoDetailPage.tsx's Sources tab — the
          question is the same shape, just reached through the block modal's
          "View PDF" here instead of a row action. */}
      {comparePair && (
        <PdfCompareModal
          left={{
            pdfId: comparePair.dailyPdfId,
            filename: comparePair.filename,
            badge: 'original',
          }}
          right={{
            pdfId: { kind: 'generated', id: comparePair.artifactId },
            filename: comparePair.filename,
            badge: `sent to ${gcName(me)}`,
          }}
          onClose={() => setComparePair(null)}
        />
      )}
    </Section>
  );
}

/**
 * Carve-out mover — pins one source block into a chosen open submission of the
 * same PCO. Only surfaced when the PCO has more than one open submission (a
 * split). Calls POST /api/blocks/assign-submission; maps the lifecycle error
 * codes to friendly copy via submissionErrorMessage.
 */
function MoveToSubmissionModal({
  block,
  submissions,
  pcoParentNumber,
  onCancel,
  onMoved,
}: {
  block: RollupBlockSummary;
  submissions: SubmissionSummary[];
  pcoParentNumber: string;
  onCancel: () => void;
  onMoved: () => Promise<void>;
}) {
  const { token, me } = useAuth();
  const [submissionId, setSubmissionId] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Mounted only while open, so the lock is unconditional.
  useBodyScrollLock(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const submissionOptions = useMemo(
    () => [
      { value: '', label: 'Select a submission…' },
      ...submissions.map((s) => ({
        value: s.id,
        label: `${formatSubmissionLabel(pcoParentNumber, s.submission_number)}${s.is_default_target ? ' (default)' : ''}`,
      })),
    ],
    [submissions, pcoParentNumber],
  );

  const confirm = async () => {
    if (!token || !submissionId) return;
    setBusy(true);
    setErr(null);
    try {
      await api.blocks.assignSubmission(token, submissionId, [block.daily_entry_id]);
      await onMoved();
    } catch (e) {
      setErr(submissionErrorMessage(e, 'Could not transfer the block.', gcName(me)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="pdf-modal-overlay"
      onClick={() => {
        if (!busy) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Transfer block"
    >
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="confirm-modal-head">
          <div className="confirm-modal-title">Transfer block</div>
        </div>
        <div className="confirm-modal-body">
          <div
            style={{
              background: 'var(--surface-warm, var(--surface))',
              border: '1px solid var(--rule)',
              borderRadius: 4,
              padding: '8px 10px',
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            <span className="mono">{block.activity_code}</span> · work date{' '}
            {formatDateShort(block.report_date)}
          </div>

          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            Destination submission
          </label>
          <Dropdown
            options={submissionOptions}
            value={submissionId}
            onChange={setSubmissionId}
            disabled={busy}
            style={{ width: '100%' }}
          />

          {err && (
            <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 8 }}>{err}</div>
          )}
        </div>
        <div className="confirm-modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-cta"
            onClick={confirm}
            disabled={busy || !submissionId}
          >
            {busy ? 'Transferring…' : 'Transfer'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Black header action that opens the manual-add popup (ADR 0004). */
function AddRowButton({ label, onClick }: { label: string; onClick: () => void }) {
  // Accent outline, matching the inbox add-line buttons (RULES.md §2) — the
  // affirmative-but-not-primary voice; solid stays with Approve & Send.
  return (
    <button
      type="button"
      className="btn btn-ghost btn-accent"
      style={{ padding: '5px 12px', fontSize: 12.5 }}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

/**
 * "MANUALLY ADDED" provenance pill (ADR 0004). Muted grey on light grey — no
 * accent color, per the design system. Permanent: stays even after edits.
 * Tooltip shows who added it, when, and why.
 */
function ManualBadge({ line }: { line: ManualProvenance }) {
  if (!line.is_manual) return null;
  const when = line.manual_added_at ? formatDateShort(line.manual_added_at.slice(0, 10)) : null;
  const tip =
    `${line.manual_added_by ? `Added by ${line.manual_added_by}` : 'Manually added'}` +
    `${when ? ` on ${when}` : ''}` +
    `${line.manual_reason ? `\nReason: ${line.manual_reason}` : ''}`;
  return (
    <span
      title={tip}
      style={{
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--ink-3)',
        border: '1px solid var(--rule)',
        padding: '1px 6px',
        borderRadius: 3,
        marginLeft: 8,
        whiteSpace: 'nowrap',
      }}
    >
      Manually added
    </span>
  );
}

// ---------------------------------------------------------------------------
// Editable row components
// ---------------------------------------------------------------------------

/**
 * Labor row. Hours and Rate are buttons, not inputs: clicking either opens
 * LineDayEditForm, which asks WHICH DAY before it writes. The line shown here
 * is a weekly aggregate and the spreadsheet bills per day, so a value typed
 * against the weekly cell can't say what it means — the form is the fix for the
 * 2026-07-31 day-ambiguity flaw, not decoration.
 *
 * Unknown employees (no roster FK) are editable too — the edit targets the
 * underlying row id, not the employee FK — but Class still needs a roster row.
 */
function LaborRow({
  line,
  totalHrs,
  lineCents,
  pcoId,
  weekEndingDate,
  editable,
  onSaved,
  onEdit,
}: {
  line: RollupDetailLaborLine;
  totalHrs: number;
  lineCents: number;
  /** Still needed by the Class edit, which is per-employee and (pco, week)-keyed.
   *  Hours/rate no longer route from here — they open LineDayEditForm. */
  pcoId: string;
  weekEndingDate: string;
  editable: boolean;
  onSaved: () => Promise<void>;
  onEdit: (focusField: 'hours' | 'rate') => void;
}) {
  const { token } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const canEdit = editable;
  const canEditClass = editable && line.employee_id !== null;

  const handleClassSave = async (newClass: string) => {
    if (!token || !line.employee_id) return;
    const trimmed = newClass.trim();
    if (trimmed === '' || trimmed === line.classification) return;
    setBusy(true);
    setErr(null);
    try {
      await api.rollups.setLaborClass(token, pcoId, weekEndingDate, line.employee_id, trimmed);
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td className="mono" style={{ color: 'var(--ink)', fontSize: 13 }}>
        {line.heavyjob_employee_id ?? '—'}
      </td>
      <td>
        {line.employee_name}
        {line.is_unknown && (
          <span style={{ color: 'var(--warning)', marginLeft: 8, fontSize: 11 }}>unknown</span>
        )}
        <ManualBadge line={line} />
      </td>
      <td className="muted">
        {canEditClass ? (
          <InlineClassSelect
            value={line.classification}
            onChange={handleClassSave}
            disabled={busy}
            ariaLabel={`${line.employee_name} class`}
          />
        ) : (
          <span
            title={
              line.is_unknown
                ? 'Unknown employee — add to roster in Settings to edit'
                : 'Rollup is locked'
            }
          >
            {line.classification}
          </span>
        )}
        {/* Class is the only write this row still makes directly — so its
            failure belongs here, next to the control that failed. It used to
            render under Rate, where it read as a rate error. */}
        {err && <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 2 }}>{err}</div>}
      </td>
      {SHOW_OT_DT_COLUMNS && <td className="num">{formatHours(line.st_hours)}</td>}
      {SHOW_OT_DT_COLUMNS && <td className="num">{formatHours(line.ot_hours)}</td>}
      {SHOW_OT_DT_COLUMNS && <td className="num">{formatHours(line.dt_hours)}</td>}
      <td className="num">
        <EditableCell
          editable={canEdit}
          label={`${line.employee_name} total hours`}
          onClick={() => onEdit('hours')}
        >
          {formatHours(totalHrs)}
        </EditableCell>
      </td>
      <td className="num">
        <EditableCell
          editable={canEdit}
          label={`${line.employee_name} rate`}
          onClick={() => onEdit('rate')}
        >
          {formatMoneyCents(line.st_rate_cents, { precise: true })}
        </EditableCell>
      </td>
      <td className="num">{formatMoneyCents(lineCents)}</td>
      {canEdit && <EditRowButton label={line.employee_name} onClick={() => onEdit('hours')} />}
    </tr>
  );
}

/**
 * A money/hours cell that opens the day-explicit edit form instead of taking a
 * value in place. Reads as an editable field (dotted underline at rest, a
 * background wash + solid accent underline on hover) but commits nothing on
 * its own — there is no save-on-blur on these cells any more, because the
 * value alone never said which day it applied to. The row's trailing pencil
 * (EditRowButton) opens the same form; this is the click-the-value shortcut.
 */
function EditableCell({
  editable,
  label,
  onClick,
  children,
}: {
  editable: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  if (!editable) {
    return <span title="Rollup is locked">{children}</span>;
  }
  return (
    <button
      type="button"
      className="line-edit-cell"
      onClick={onClick}
      aria-label={`Edit ${label}`}
      title="Edit by day"
    >
      {children}
    </button>
  );
}

/**
 * Trailing pencil column on the Labor/Equipment tables — a dedicated, always-
 * visible affordance that opens LineDayEditForm (default focus: hours), so
 * editability doesn't depend on a PM noticing the hours/rate cells are
 * clickable. Square-pen glyph rather than the plain pencil used elsewhere
 * (Settings, inbox tables): client feedback was that the plain pencil at rest
 * read as barely-there, and this is a purpose-built "edit this row" control
 * rather than a repeated per-cell action, so it earns its own glyph. Plain
 * `.icon-btn` (not `.muted`) for the same reason — ink-3 at rest is legible
 * on its own, not just on hover.
 */
function EditRowButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <td className="num">
      <button
        type="button"
        className="icon-btn"
        onClick={onClick}
        aria-label={`Edit ${label}`}
        title="Edit hours & rate"
      >
        <SquarePen size={14} aria-hidden />
      </button>
    </td>
  );
}

/**
 * Equipment row. Same shape as LaborRow: Hours and Rate open the day-explicit
 * edit form rather than taking a value against the weekly aggregate.
 */
function EquipmentRow({
  line,
  lineCents,
  editable,
  onEdit,
}: {
  line: RollupDetailEquipmentLine;
  lineCents: number;
  editable: boolean;
  onEdit: (focusField: 'hours' | 'rate') => void;
}) {
  return (
    <tr>
      <td className="mono" style={{ color: 'var(--ink)' }}>
        {line.asset_id}
        {line.is_unknown && (
          <span style={{ color: 'var(--warning)', marginLeft: 8, fontSize: 11 }}>unknown</span>
        )}
        <ManualBadge line={line} />
      </td>
      <td className="muted">{line.description}</td>
      <td className="num">
        <EditableCell
          editable={editable}
          label={`${line.asset_id} hours`}
          onClick={() => onEdit('hours')}
        >
          {formatHours(line.hours)}
        </EditableCell>
      </td>
      <td className="num">
        <EditableCell
          editable={editable}
          label={`${line.asset_id} rate`}
          onClick={() => onEdit('rate')}
        >
          {formatMoneyCents(line.rate_cents, { precise: true })}
        </EditableCell>
      </td>
      <td className="num">{formatMoneyCents(lineCents)}</td>
      {editable && <EditRowButton label={line.asset_id} onClick={() => onEdit('hours')} />}
    </tr>
  );
}

/**
 * Material row with editable Qty + Rate. Unlike labor/equipment, materials
 * don't aggregate across days — one RollupDetailMaterialLine maps 1:1 to
 * one material_queue_items row. So edits write directly via the existing
 * /api/materials/queue/:id/confirm endpoint with new quantity + rate_cents.
 */
function MaterialRow({
  line,
  editable,
  onSaved,
}: {
  line: RollupDetailMaterialLine;
  editable: boolean;
  onSaved: () => Promise<void>;
}) {
  const { token } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [qty, setQty] = useState<number>(line.quantity);
  const [rateCents, setRateCents] = useState<number>(line.rate_cents);
  const lineCents = Math.round(qty * rateCents);

  const save = async (nextQty: number, nextRate: number) => {
    if (!token) return;
    if (nextQty <= 0) {
      setErr('Quantity must be > 0');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.materials.confirm(token, line.material_queue_item_id, {
        quantity: nextQty,
        rate_cents: nextRate,
      });
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td className="name serif">
        {line.name}
        <ManualBadge line={line} />
      </td>
      <td className="num">
        {editable ? (
          <InlineNumberInput
            value={qty}
            decimals={2}
            disabled={busy}
            onChange={(v) => {
              setQty(v);
              void save(v, rateCents);
            }}
            ariaLabel={`${line.name} quantity`}
            width={80}
          />
        ) : (
          line.quantity
        )}
      </td>
      <td className="num">
        {editable ? (
          <InlineNumberInput
            value={rateCents / 100}
            decimals={2}
            disabled={busy}
            onChange={(v) => {
              const c = Math.round(v * 100);
              setRateCents(c);
              void save(qty, c);
            }}
            ariaLabel={`${line.name} rate`}
            width={92}
          />
        ) : (
          formatMoneyCents(line.rate_cents, { precise: true })
        )}
        {err && (
          <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 2 }}>{err}</div>
        )}
      </td>
      <td className="num">{formatMoneyCents(lineCents)}</td>
    </tr>
  );
}

/**
 * Failure copy for the subcontractor-line and invoice endpoints. Without this
 * the PM reads the raw machine code — ApiCallError's message IS `payload.error`
 * — so "rollup_locked" is literally what lands under the row.
 *
 * The 409 is the one that earns the words. Every control here is hidden the
 * moment the rollup isn't `in_progress`, so a PM only ever meets that error in
 * a RACE: the week was open when this page loaded and was sent (or approved,
 * or carried into a closed package) from somewhere else before the click
 * landed. Generic copy makes that look like a bug in the button. Naming the
 * event and the remedy matches how rollup_locked reads everywhere else
 * (`lib/useSubmissions.ts`, `MoveBlockModal`).
 *
 * Exhaustive in both directions, which takes BOTH ends being typed: the API's
 * subcontractor/invoice routes emit `SubcontractorInvoiceErrorCode`, not bare
 * strings (`routes/rollups.ts`, `SubcontractorRouteError` — the preflights'
 * `error` fields, the exec* 404 bodies, and the PDF-rejection code are all
 * annotated), and this map is `Record<>`-keyed off the same union. So a new
 * route code doesn't compile until it joins the union, and joining the union
 * doesn't compile until it has words here.
 *
 * Two families are deliberately outside that contract and fall through to the
 * caller's fallback, exactly as they do today: `invalid_json` (malformed
 * request, not a state the PM can act on) and the add/patch body validators'
 * field codes, which the forms surface themselves.
 */
const SUBCONTRACTOR_ERROR_MESSAGES: Record<SubcontractorInvoiceErrorCode, (gc: string) => string> = {
  rollup_locked: (gc) =>
    `This week was sent to ${gc} while you had it open, so it's locked now. Reopen the week to make this change.`,
  rollup_not_found: () => 'This week no longer exists. Refresh the page.',
  line_not_found: () => 'That subcontractor line was already removed. Refresh the page.',
  invalid_pdf: () => "That file isn't a PDF. Save the invoice as a PDF and try again.",
  invoice_too_large: () => 'That PDF is over the 10 MB limit.',
  no_invoice: () => 'That line no longer has an invoice on it. Refresh the page.',
};

function subcontractorErrorMessage(e: unknown, fallback: string, gc: string): string {
  if (e instanceof ApiCallError) {
    const code = e.payload.error as SubcontractorInvoiceErrorCode | undefined;
    const copy = code ? SUBCONTRACTOR_ERROR_MESSAGES[code] : undefined;
    if (copy) return copy(gc);
  }
  return e instanceof Error ? e.message : fallback;
}

/**
 * Subcontractor row — one outside-sub vendor invoice billed as a lump.
 * Rollup-level (no block behind it), so edits go through the dedicated
 * /by-rollup/:id/subcontractors endpoints.
 *
 * The row READS. Description, date and amount are text; the pencil opens
 * SubcontractorEditModal, which is the only thing that writes them. Every line
 * is PM-added, so the "Manually added" badge always renders.
 */
function SubcontractorRow({
  line,
  rollupId,
  weekEndingDate,
  editable,
  onSaved,
  onViewInvoice,
}: {
  line: RollupDetailSubcontractorLine;
  /** Canonical rollup id — non-null whenever lines exist (they hang off the
   *  persisted row); the null guard is TS narrowing, not a real branch. */
  rollupId: string | null;
  weekEndingDate: string;
  editable: boolean;
  onSaved: () => Promise<void>;
  /** Hands the invoice to the page's single PDF viewer. */
  onViewInvoice: (source: PdfSource) => void;
}) {
  const { token, me } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const days = useMemo(() => weekDays(weekEndingDate), [weekEndingDate]);
  const dayOptions = useMemo(
    () => days.map((d) => ({ value: d.value, label: d.label })),
    [days],
  );
  const canEdit = editable && rollupId !== null;

  const remove = async () => {
    if (!token || !rollupId) return;
    if (
      !window.confirm(
        `Remove "${line.description}" (${formatMoneyCents(line.amount_cents)}) from this rollup?`,
      )
    ) {
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.rollups.removeSubcontractor(token, rollupId, line.id);
      await onSaved();
    } catch (e) {
      setErr(subcontractorErrorMessage(e, 'Remove failed', gcName(me)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td className="name serif">
        {line.description}
        <ManualBadge line={line} />
        <SubcontractorInvoiceControls
          line={line}
          rollupId={rollupId}
          canEdit={canEdit}
          busy={busy}
          onSaved={onSaved}
          onView={onViewInvoice}
        />
      </td>
      <td className="muted">
        {line.invoice_date ? formatDateShort(line.invoice_date) : '—'}
      </td>
      <td className="num">
        {formatMoneyCents(line.amount_cents)}
        {/* A vendor credit (2026-09-25) keeps the plain money voice — the minus
            carries it; red is for delete/reject (RULES.md §2.7). The word is
            not money, so it drops the cell's mono face (§5.1). */}
        {line.amount_cents < 0 && (
          <span
            className="muted"
            style={{ fontFamily: 'var(--sans)', fontSize: 11.5, marginLeft: 6 }}
          >
            credit
          </span>
        )}
        {err && (
          <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 2 }}>{err}</div>
        )}
      </td>
      {editable && (
        <td className="num">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 4,
            }}
          >
            {/* Description, date and amount used to be three live inputs sitting
                open in the row (client, 2026-08-03: "this is just wide open to
                edit"). They read now, and this opens the form that writes —
                the same read-then-edit shape the Labor/Equipment rows take. */}
            <button
              type="button"
              className="icon-btn muted"
              onClick={() => setEditOpen(true)}
              disabled={busy || !canEdit}
              aria-label={`Edit ${line.description}`}
              title="Edit line"
            >
              <SquarePen size={14} aria-hidden />
            </button>
            {/* The same trash as the invoice control under the description, and
                deliberately so (client, 2026-07-30): a destructive action should
                look destructive, which the old × text link never did. This one
                takes the LINE and its money — `title` + `aria-label` are what
                tell the two apart. `.danger` alone, for the hover-order reason
                spelled out in the invoice controls below. */}
            <button
              type="button"
              className="icon-btn danger"
              onClick={() => void remove()}
              disabled={busy}
              aria-label={`Remove ${line.description}`}
              title="Remove line"
            >
              <Trash2 size={14} aria-hidden />
            </button>
          </span>
          {editOpen && canEdit && rollupId !== null && (
            <SubcontractorEditModal
              line={line}
              rollupId={rollupId}
              dayOptions={dayOptions}
              onClose={() => setEditOpen(false)}
              onSaved={onSaved}
            />
          )}
        </td>
      )}
    </tr>
  );
}

const SUB_EDIT_INPUT_STYLE: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: 13,
  border: '1px solid var(--rule-strong, var(--rule))',
  borderRadius: 4,
  background: 'var(--surface)',
  color: 'var(--ink)',
};

const SUB_EDIT_LABEL_STYLE: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--ink-3)',
};

/**
 * Edit one subcontractor line — description, invoice date, amount.
 *
 * Same endpoint and same payload semantics the row's per-field blur commits
 * had: only the fields that actually moved are sent, and a form closed without
 * a change writes nothing (the endpoint refuses an empty patch anyway). The
 * amount keeps MoneyInput rather than a bare number field — these are large
 * lumps where comma grouping matters, and it is also what makes the parse
 * ("$3,000,000.00" → 300000000 cents) identical to before. Any non-zero amount
 * saves: a negative one is a vendor credit (2026-09-25); zero is refused.
 *
 * Portalled to document.body rather than rendered where it is invoked: the row
 * cell it hangs off carries the table's mono face, 12.5px size and right
 * alignment, and a modal rendered inside one inherits all three.
 */
function SubcontractorEditModal({
  line,
  rollupId,
  dayOptions,
  onClose,
  onSaved,
}: {
  line: RollupDetailSubcontractorLine;
  rollupId: string;
  /** The week's Sun–Sat days — same options the row's inline Dropdown had. */
  dayOptions: { value: string; label: string }[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { token, me } = useAuth();
  const [desc, setDesc] = useState(line.description);
  const [invoiceDate, setInvoiceDate] = useState(line.invoice_date);
  const [amountRaw, setAmountRaw] = useState((line.amount_cents / 100).toFixed(2));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useBodyScrollLock(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const trimmed = desc.trim();
  const cents = Math.round(Number(amountRaw || '0') * 100);
  const valid = trimmed !== '' && Number.isFinite(cents) && cents !== 0 && !busy;

  const handleSave = async () => {
    if (!token || !valid) return;
    const body: SubcontractorLinePatchRequest = {};
    if (trimmed !== line.description) body.description = trimmed;
    if (cents !== line.amount_cents) body.amount_cents = cents;
    if (invoiceDate !== line.invoice_date) body.invoice_date = invoiceDate;
    // Nothing moved — the old per-field commits returned early on a no-op too,
    // and the endpoint would answer `no_fields`.
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.rollups.updateSubcontractor(token, rollupId, line.id, body);
      await onSaved();
      onClose();
    } catch (e) {
      setErr(subcontractorErrorMessage(e, 'Save failed', gcName(me)));
    } finally {
      setBusy(false);
    }
  };

  const title = 'Edit subcontractor line';

  return createPortal(
    <div
      className="pdf-modal-overlay"
      onClick={() => {
        if (!busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="confirm-modal-head">
          <div className="confirm-modal-title">{title}</div>
        </div>
        <div
          className="confirm-modal-body"
          style={{ display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'left' }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={SUB_EDIT_LABEL_STYLE}>Description</span>
            <input
              style={{ ...SUB_EDIT_INPUT_STYLE, width: '100%' }}
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              disabled={busy}
              autoFocus
            />
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            <label
              style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}
            >
              <span style={SUB_EDIT_LABEL_STYLE}>Invoice date</span>
              <Dropdown
                style={{ width: '100%' }}
                options={dayOptions}
                value={invoiceDate}
                onChange={setInvoiceDate}
                disabled={busy}
                aria-label="Invoice date"
              />
            </label>
            <label
              style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}
            >
              <span style={SUB_EDIT_LABEL_STYLE}>Amount ($)</span>
              <MoneyInput
                value={amountRaw}
                onChange={setAmountRaw}
                disabled={busy}
                style={{ ...SUB_EDIT_INPUT_STYLE, width: '100%' }}
              />
            </label>
          </div>
          {err && <div style={{ color: 'var(--danger)', fontSize: 12.5 }}>{err}</div>}
        </div>
        <div className="confirm-modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-cta"
            onClick={() => void handleSave()}
            disabled={!valid}
          >
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * The line's vendor invoice PDF — one per line, because a line IS one vendor
 * invoice (ADR 0009). Approve & Send attaches it alongside the daily T&M PDFs,
 * so "no invoice" on an open week is a real to-do, not a cosmetic gap; that's
 * why the absent state is stated rather than left blank.
 *
 * On a LOCKED rollup the actions disappear but View stays — reviewing what
 * went to the GC is legitimate, and the API allows the read on any status.
 *
 * The affordances are icons, not text links: they repeat on every line, and
 * spelled-out labels buried the description and the money they hang under. Same
 * control family as the Settings row actions, at the same 26px/14px size —
 * `.sm` exists for controls riding a text baseline inside a cell, and these get
 * their own line under the description, where 20px/13px read as specks
 * (client, 2026-07-30).
 */
function SubcontractorInvoiceControls({
  line,
  rollupId,
  canEdit,
  busy,
  onSaved,
  onView,
}: {
  line: RollupDetailSubcontractorLine;
  rollupId: string | null;
  canEdit: boolean;
  busy: boolean;
  onSaved: () => Promise<void>;
  onView: (source: PdfSource) => void;
}) {
  const { token, me } = useAuth();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [working, setWorking] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const has = line.has_invoice_pdf === true;
  const disabled = busy || working;

  // Straight into the page's in-app viewer — same overlay the daily PDFs use.
  // The URL is minted by the viewer's own hook, so the fetch's loading and
  // failure states are shown where the reader is already looking. Clearing `err`
  // still belongs here: a red row error from an earlier upload would otherwise
  // outlive the action that replaced it.
  const view = () => {
    if (!rollupId) return;
    setErr(null);
    onView({ kind: 'subcontractor-invoice', rollupId, lineId: line.id });
  };

  const upload = async (file: File) => {
    if (!token || !rollupId) return;
    if (file.size > SUBCONTRACTOR_INVOICE_MAX_BYTES) {
      setErr(`That PDF is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is 10 MB.`);
      return;
    }
    setWorking(true);
    setErr(null);
    try {
      await api.rollups.uploadSubcontractorInvoice(token, rollupId, line.id, file);
      await onSaved();
    } catch (e) {
      setErr(subcontractorErrorMessage(e, 'Upload failed', gcName(me)));
    } finally {
      setWorking(false);
    }
  };

  const removeInvoice = async () => {
    if (!token || !rollupId) return;
    if (!window.confirm(`Remove the invoice PDF from "${line.description}"?`)) return;
    setWorking(true);
    setErr(null);
    try {
      await api.rollups.removeSubcontractorInvoice(token, rollupId, line.id);
      await onSaved();
    } catch (e) {
      setErr(subcontractorErrorMessage(e, 'Remove failed', gcName(me)));
    } finally {
      setWorking(false);
    }
  };

  // Locked + no invoice: say nothing. There is no action to take and the
  // absence is already history.
  if (!has && !canEdit) return null;

  return (
    <div style={{ fontSize: 11, marginTop: 2 }}>
      {canEdit && (
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,.pdf"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = ''; // allow re-picking the same file after a failure
            if (f) void upload(f);
          }}
        />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {has ? (
          <>
            <button
              type="button"
              className="icon-btn muted"
              onClick={view}
              disabled={disabled}
              aria-label={`View the invoice PDF for ${line.description}`}
              title="View invoice"
            >
              <FileText size={14} aria-hidden />
            </button>
            {canEdit && (
              <>
                <button
                  type="button"
                  className="icon-btn muted"
                  onClick={() => fileRef.current?.click()}
                  disabled={disabled}
                  aria-label={`Replace the invoice PDF on ${line.description}`}
                  title="Replace invoice"
                >
                  <Upload size={14} aria-hidden />
                </button>
                {/* No `.muted` on the destructive one: its hover rule is defined
                    after `.danger`'s and at the same specificity, so the pair
                    would cost the red hover the trash exists to signal. */}
                <button
                  type="button"
                  className="icon-btn danger"
                  onClick={() => void removeInvoice()}
                  disabled={disabled}
                  aria-label={`Remove the invoice PDF from ${line.description}`}
                  title="Remove invoice"
                >
                  <Trash2 size={14} aria-hidden />
                </button>
              </>
            )}
          </>
        ) : (
          <>
            <span className="muted">No invoice</span>
            <button
              type="button"
              className="icon-btn muted"
              onClick={() => fileRef.current?.click()}
              disabled={disabled}
              aria-label={`Attach an invoice PDF to ${line.description}`}
              title="Attach invoice"
            >
              <Paperclip size={14} aria-hidden />
            </button>
          </>
        )}
        {working && <span className="muted">working…</span>}
      </div>
      {err && <div style={{ color: 'var(--danger)', marginTop: 2 }}>{err}</div>}
    </div>
  );
}
